/**
 * ThemeContext — V2.0 Foundation
 *
 * Provides a light/dark/system theme selector to the V2 design system, backed
 * by localStorage under the key `iaos_theme_pref`. Applies the `dark` class
 * on <html> when the effective theme is dark; strips it otherwise.
 *
 * Safe-by-design guarantees for the V1.1 rollout:
 * 1. Default preference is "light" — no `dark` class is applied on cold mount,
 *    so any legacy Tailwind `dark:` variants stay inactive.
 * 2. Legacy V1.1 surfaces once rendered with hard-coded CSS variables
 *    independent of the `dark` class. As of V2.0 Phase 5 Batch C4, all
 *    active surfaces have migrated to the `--v2-*` token space, which
 *    responds to the `dark` class on <html>.
 *
 * Public API:
 *   const { theme, effectiveTheme, setTheme } = useTheme();
 *   //   theme:          "light" | "dark" | "system"
 *   //   effectiveTheme: "light" | "dark"   (system resolved to concrete value)
 *   //   setTheme(next): setter (persists to localStorage)
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "iaos_theme_pref";
const VALID = new Set(["light", "dark", "system"]);

const ThemeContext = createContext({
  theme: "light",
  effectiveTheme: "light",
  setTheme: () => {},
});

function readInitial() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && VALID.has(raw)) return raw;
  } catch (_) {
    /* localStorage unavailable — fall through to default */
  }
  return "light";
}

function systemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyDocumentClass(effective) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (effective === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitial);
  const [effectiveTheme, setEffective] = useState(() =>
    readInitial() === "system"
      ? (systemPrefersDark() ? "dark" : "light")
      : readInitial()
  );

  // Whenever theme changes, resolve effective and apply doc class + persist.
  useEffect(() => {
    const eff = theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme;
    setEffective(eff);
    applyDocumentClass(eff);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* ignore */
    }
  }, [theme]);

  // While in "system" mode, react to OS-preference changes.
  useEffect(() => {
    if (theme !== "system") return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e) => {
      const eff = e.matches ? "dark" : "light";
      setEffective(eff);
      applyDocumentClass(eff);
    };
    if (mq.addEventListener) mq.addEventListener("change", listener);
    else if (mq.addListener) mq.addListener(listener);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", listener);
      else if (mq.removeListener) mq.removeListener(listener);
    };
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!VALID.has(next)) return;
    setThemeState(next);
  }, []);

  const value = useMemo(
    () => ({ theme, effectiveTheme, setTheme }),
    [theme, effectiveTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
